/**
 * A contact's activities, on their profile.
 *
 * The panel that finally makes the two services visible as one thing to a user:
 * these rows come from the scheduling service, this screen came from the CRM,
 * and the person reading it has no reason to know that.
 */
import { useQuery } from '@apollo/client';
import { activityPalette } from '@meshhub/design-tokens/tokens';
import { Button } from '@meshhub/design-tokens/ui';
import { CalendarPlus } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { ACTIVITIES_QUERY, ACTIVITY_TYPES_QUERY } from '../../lib/queries.js';
import { ACTIVITY_STATUS_LABELS, formatDateTime } from '../../lib/format.js';
import { S } from '../../lib/strings.js';
import { ActivityDialog } from '../calendar/ActivityDialog.web.jsx';
import { LoadingOrEmpty } from '../FormKit.web.jsx';

export function ActivitiesPanel({ contactId }) {
  const [dialog, setDialog] = useState(null);

  const { data, loading, refetch } = useQuery(ACTIVITIES_QUERY, {
    variables: { contactId, page: 1, pageSize: 25 },
    skip: !contactId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: typeData } = useQuery(ACTIVITY_TYPES_QUERY);

  const colorForType = useCallback(
    (typeId) => {
      const types = typeData?.activityTypes ?? [];
      const index = types.findIndex((type) => type.id === typeId);
      return activityPalette[(index < 0 ? 0 : index) % activityPalette.length];
    },
    [typeData],
  );

  const activities = data?.activities?.items ?? [];

  return (
    <section className="flex flex-col gap-3 rounded border border-border bg-surface p-4">
      <header className="flex items-center gap-3">
        <h2 className="text-sm font-medium">{S.activity.activitiesTab}</h2>
        <Button
          type="button"
          className="ms-auto"
          onClick={() => setDialog({ prefill: { contactId } })}
        >
          <CalendarPlus size={14} aria-hidden="true" />
          {S.calendar.newActivity}
        </Button>
      </header>

      <LoadingOrEmpty
        loading={loading && activities.length === 0}
        empty={!loading && activities.length === 0}
        emptyTitle={S.activity.noActivities}
      />

      <ul className="flex flex-col gap-1">
        {activities.map((activity) => (
          <li key={activity.id}>
            <button
              type="button"
              onClick={() => setDialog({ activity })}
              className="flex w-full items-center gap-3 rounded border-s-4 px-3 py-2 text-start
                         text-sm hover:bg-surface-hover"
              style={{ borderInlineStartColor: colorForType(activity.type?.id) }}
            >
              <span className="flex-1 truncate">{activity.title}</span>
              <span className="text-xs text-text-muted">
                {formatDateTime(activity.startsAt)}
              </span>
              <span
                className={[
                  'rounded px-2 py-px text-xs',
                  activity.status === 'MISSED'
                    ? 'bg-danger text-text-inverse'
                    : 'bg-surface-hover text-text-muted',
                ].join(' ')}
              >
                {ACTIVITY_STATUS_LABELS[activity.status]}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <ActivityDialog
        open={Boolean(dialog)}
        onOpenChange={(open) => !open && setDialog(null)}
        activity={dialog?.activity}
        prefill={dialog?.prefill}
        onSaved={() => refetch()}
      />
    </section>
  );
}
